import React, { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import BlockIcon from '@mui/icons-material/Block';
import { BasePage } from '../../components/base-page';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  GetMoneyTransactionsForAccountDocument,
  GetMoneyTransactionsForAccountQuery,
  MoneyTransactionFragmentFragment,
  UpdateMoneyTransactionDocument,
} from '../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';
import { MoneyTransactionDialog } from './components/money-transaction-dialog';
import { PaymentAssignmentDialog } from './components/payment-assignment-dialog';
import { ExpenseAssignmentDialog } from './components/expense-assignment-dialog';
import { TextBody1 } from '../../components/text-body-1';
import { useSearchParams } from 'react-router';

const FilterGroupsArray = ['all', 'unreconciled'] as const;
type FilterGroupType = (typeof FilterGroupsArray)[number];

export function MoneyTransactionListPage() {
  const { show } = useAlert();
  const theme = useTheme();
  const app = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<string>('');
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [transactionToAssign, setTransactionToAssign] = useState<MoneyTransactionFragmentFragment | null>(null);
  const [expenseAssignmentDialogOpen, setExpenseAssignmentDialogOpen] = useState(false);
  const [transactionToAssignExpense, setTransactionToAssignExpense] = useState<MoneyTransactionFragmentFragment | null>(
    null,
  );

  const filterGroup: FilterGroupType = (searchParams.get('fg') as FilterGroupType) ?? 'all';
  if (filterGroup && !FilterGroupsArray.includes(filterGroup as FilterGroupType)) {
    searchParams.set('fg', 'all');
    setSearchParams(searchParams);
  }

  useEffect(() => {
    if (filterGroup === 'unreconciled') {
      setShowUnreconciledOnly(true);
    } else {
      setShowUnreconciledOnly(false);
    }
  }, [filterGroup]);

  const { data, loading, error, refetch } = useQuery<GetMoneyTransactionsForAccountQuery>(
    GetMoneyTransactionsForAccountDocument,
    {
      variables: {
        filter: {
          showUnreconciledOnly,
        },
      },
    },
  );

  const [updateTransaction] = useMutation(UpdateMoneyTransactionDocument);

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať transakcie.', error.message);
    }
  }, [error, show]);

  const transactions = data?.getMoneyTransactionsForAccount ?? [];

  const handleNoteClick = (note: string) => {
    setSelectedNote(note);
    setNoteDialogOpen(true);
  };

  const handleAssignPayment = (transaction: MoneyTransactionFragmentFragment) => {
    setTransactionToAssign(transaction);
    setAssignmentDialogOpen(true);
  };

  const handleAssignExpensePayment = (transaction: MoneyTransactionFragmentFragment) => {
    setTransactionToAssignExpense(transaction);
    setExpenseAssignmentDialogOpen(true);
  };

  const handleToggleDoNotReconcile = async (transaction: MoneyTransactionFragmentFragment) => {
    try {
      await updateTransaction({
        variables: {
          input: {
            id: transaction.id,
            doNotReconcile: !transaction.doNotReconcile,
          },
        },
      });
      refetch();
    } catch (err: any) {
      show.error('Nepodarilo sa aktualizovať transakciu.', err.message);
    }
  };

  const getReconciledStatusColor = (reconciledValue: number, totalValue: number, doNotReconcile?: boolean) => {
    if (doNotReconcile) return 'disabled'; // gray
    if (reconciledValue <= 0) return 'error'; // red - not reconciled
    if (reconciledValue < totalValue) return 'info'; // blue - partially reconciled
    return 'success'; // green - fully reconciled
  };

  const getDisplayValue = (value: number, type: string): number => {
    return type === 'DEBIT' ? -Math.abs(value) : value;
  };

  const columns = React.useMemo((): GridColDef<MoneyTransactionFragmentFragment>[] => {
    return [
      {
        display: 'flex',
        field: 'noteIcon',
        headerName: '',
        width: 30,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<MoneyTransactionFragmentFragment>) => {
          const c = getReconciledStatusColor(
            params.row.reconciledValue,
            params.row.value,
            params.row.doNotReconcile || undefined,
          );
          if (c === 'success') return;
          return (
            <Stack direction={'row'} alignSelf={'center'} spacing={0.5} alignItems="center">
              <CircleIcon sx={{ fontSize: 'small' }} color={c} />
              {params.row.note && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNoteClick(params.row.note || '');
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'transactionDate',
        headerName: 'Dátum',
        type: 'string',
        valueFormatter: (value: string) => app.formatDate(value),
        width: 130,
      },
      {
        field: 'type',
        headerName: 'Typ',
        width: 100,
        valueFormatter: (value: string) => (value === 'CREDIT' ? 'Príjem' : 'Výdavok'),
      },
      {
        field: 'value',
        headerName: 'Suma',
        width: 120,
        valueFormatter: (value: number, row: MoneyTransactionFragmentFragment) => {
          const displayValue = getDisplayValue(value, row.type);
          return app.formatNumber(displayValue, 2) + ' €';
        },
        renderCell: (params: GridRenderCellParams<MoneyTransactionFragmentFragment>) => {
          const displayValue = getDisplayValue(params.row.value, params.row.type);
          const formattedValue = app.formatNumber(displayValue, 2) + ' €';
          return (
            <span style={{ color: params.row.type === 'DEBIT' ? theme.palette.error.main : 'inherit' }}>
              {formattedValue}
            </span>
          );
        },
      },
      {
        field: 'otherAccountNo',
        headerName: 'Protiúčet',
        width: 180,
        flex: 1,
      },
      {
        field: 'reference',
        headerName: 'Var. symbol',
        width: 120,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Akcie',
        width: 80,
        getActions: (params) => {
          const transaction = params.row;
          const actions = [];

          // Only show assign action for CREDIT transactions with unreconciled value
          if (transaction.type === 'CREDIT' && transaction.reconciledValue < transaction.value) {
            actions.push(
              <GridActionsCellItem
                key="assign"
                icon={<LinkIcon />}
                label="Priraď úhradu"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignPayment(transaction);
                }}
                showInMenu
              />,
            );
          }

          // Only show assign expense action for DEBIT transactions with unreconciled value
          if (transaction.type === 'DEBIT' && transaction.reconciledValue < transaction.value) {
            actions.push(
              <GridActionsCellItem
                key="assignExpense"
                icon={<LinkIcon />}
                label="Priraď platbu"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAssignExpensePayment(transaction);
                }}
                showInMenu
              />,
            );
          }

          // Add "Do not reconcile" action for unreconciled transactions
          if (transaction.reconciledValue < transaction.value) {
            actions.push(
              <GridActionsCellItem
                key="doNotReconcile"
                icon={<BlockIcon />}
                label={transaction.doNotReconcile ? '✓ Neriešiť' : 'Neriešiť'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleDoNotReconcile(transaction);
                }}
                showInMenu
              />,
            );
          }

          return actions;
        },
      },
    ];
  }, [app, theme]);

  const handleClose = () => {
    setSelectedTransaction(null);
    setCreateDialogOpen(false);
  };

  const handleDialogAssignPayment = () => {
    const transaction = transactions.find((t) => t.id === selectedTransaction);
    if (!transaction) return;
    handleClose();
    if (transaction.type === 'CREDIT') {
      handleAssignPayment(transaction);
    } else {
      handleAssignExpensePayment(transaction);
    }
  };

  const handleAssignmentDialogClose = (doRefresh: boolean) => {
    setTransactionToAssign(null);
    setAssignmentDialogOpen(false);
    if (doRefresh) {
      refetch();
    }
  };

  const handleExpenseAssignmentDialogClose = (doRefresh: boolean) => {
    setTransactionToAssignExpense(null);
    setExpenseAssignmentDialogOpen(false);
    if (doRefresh) {
      refetch();
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <BasePage title="Peniaze" loading={loading} errorMsg={error ? 'Nepodarilo sa načítať transakcie.' : undefined}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h2">Peňažné transakcie</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            Pridať transakciu
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant={filterGroup != 'all' ? 'outlined' : 'contained'}
            onClick={() => {
              searchParams.set('fg', 'all');
              setSearchParams(searchParams);
            }}
          >
            Všetky
          </Button>
          <Button
            variant={filterGroup != 'unreconciled' ? 'outlined' : 'contained'}
            onClick={() => {
              searchParams.set('fg', 'unreconciled');
              setSearchParams(searchParams);
            }}
          >
            Iba nepriradené
          </Button>
        </Stack>

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid<MoneyTransactionFragmentFragment>
            rows={transactions}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                },
              },
              sorting: {
                sortModel: [{ field: 'transactionDate', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedTransaction(params.row.id)}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </Box>
      </Box>

      {(selectedTransaction || createDialogOpen) && (
        <MoneyTransactionDialog
          open={!!selectedTransaction || createDialogOpen}
          transaction={transactions.find((t) => t.id === selectedTransaction) || null}
          onClose={handleClose}
          onRefresh={handleRefresh}
          onAssignPayment={selectedTransaction ? handleDialogAssignPayment : undefined}
        />
      )}

      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Poznámka
            <IconButton onClick={() => setNoteDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextBody1>{selectedNote}</TextBody1>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Zavrieť</Button>
        </DialogActions>
      </Dialog>

      <PaymentAssignmentDialog
        open={assignmentDialogOpen}
        transaction={transactionToAssign}
        onClose={(doRefresh: boolean) => handleAssignmentDialogClose(doRefresh)}
        onRefresh={handleRefresh}
      />

      <ExpenseAssignmentDialog
        open={expenseAssignmentDialogOpen}
        transaction={transactionToAssignExpense}
        onClose={(doRefresh: boolean) => handleExpenseAssignmentDialogClose(doRefresh)}
        onRefresh={handleRefresh}
      />
    </BasePage>
  );
}
